import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { moduleAccessibilityService as service } from '../../_services/moduleAccessibility.service';

const normalizeIds = values => [...new Set(values.map(Number))].sort((left, right) => left - right);
const sameIds = (left, right) => JSON.stringify(normalizeIds(left)) === JSON.stringify(normalizeIds(right));

export function CompanyModulePermissions({ company, onDirtyChange, onBusyChange }) {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);
    const dirty = useRef(false);
    const busy = useRef(false);
    const denied = useRef(false);
    const mounted = useRef(true);
    const sequence = useRef(0);
    const refreshRef = useRef(null);
    const hasChanges = data !== null && !sameIds(draft, data.permissionIds);

    useEffect(() => {
        mounted.current = true;
        let disposed = false;
        let pending = false;

        const accept = result => {
            setData(result);
            setDraft(normalizeIds(result.permissionIds));
            dirty.current = false;
            onDirtyChange(false);
        };

        const refresh = (force = true, discard = false) => {
            if (disposed || pending || busy.current || denied.current || (dirty.current && !discard)) return;

            const version = ++sequence.current;
            pending = true;
            setRefreshing(true);
            const result = service.details(company, force);

            if (result.data !== null && !discard) accept(result.data);

            result.revalidate.then(value => {
                if (!disposed && version === sequence.current) {
                    accept(value);
                    setError('');

                    if (discard) setMessage('');
                }
            }).catch(err => {

                if (!disposed && version === sequence.current) {
                    setError(err.message);

                    if (err.status === 401 || err.status === 403) {
                        denied.current = true;
                        setAccessDenied(true);
                    }
                }
            }).finally(() => {
                pending = false;

                if (!disposed) setRefreshing(false);
            });
        };

        refreshRef.current = refresh;
        refresh(false);

        const refreshVisible = () => {
            if (document.visibilityState === 'visible' && navigator.onLine !== false) refresh();
        };

        const timer = window.setInterval(refreshVisible, 30000);
        window.addEventListener('focus', refreshVisible);
        window.addEventListener('online', refreshVisible);
        document.addEventListener('visibilitychange', refreshVisible);

        return () => {
            disposed = true;
            mounted.current = false;
            sequence.current += 1;
            window.clearInterval(timer);
            window.removeEventListener('focus', refreshVisible);
            window.removeEventListener('online', refreshVisible);
            document.removeEventListener('visibilitychange', refreshVisible);
        };
    }, [company, onDirtyChange]);

    function toggle(id, checked) {
        const next = normalizeIds(checked ? [...draft, id] : draft.filter(value => value !== id));
        sequence.current += 1;
        dirty.current = !sameIds(next, data.permissionIds);
        onDirtyChange(dirty.current);
        setDraft(next);
        setMessage('');
    }

    async function save() {
        if (busy.current || !hasChanges) return;

        const previous = data;
        busy.current = true;
        sequence.current += 1;
        setSaving(true);
        onBusyChange(true);
        setError('');
        setMessage('');
        // Show the requested assignment immediately, restoring the baseline on failure.
        setData({ ...data, permissionIds: draft });
        dirty.current = false;
        onDirtyChange(false);

        try {
            const saved = await service.save(company, draft, normalizeIds(previous.permissionIds));

            if (mounted.current) {
                setData(saved);
                setDraft(normalizeIds(saved.permissionIds));
                setMessage('Module accessibility saved.');
            }
        } catch (err) {
            if (mounted.current) {
                setData(previous);
                dirty.current = true;
                onDirtyChange(true);
                setError(err.message);

                if (err.status === 401 || err.status === 403) {
                    denied.current = true;
                    setAccessDenied(true);
                }
            }
        } finally {
            busy.current = false;

            if (mounted.current) {
                setSaving(false);
                onBusyChange(false);
            }
        }
    }

    if (accessDenied) return <Alert variant="danger">Access denied. {error}</Alert>;
    
    return (
        <>
            {error && <Alert variant="danger">{error}</Alert>}
            {message && <Alert variant="success">{message}</Alert>}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h3 className="h5 mb-0">Modules</h3>
                <Button variant="outline-secondary" disabled={saving || refreshing || hasChanges} onClick={() => refreshRef.current()}>Refresh</Button>
            </div>
            {data === null ? (refreshing && <div role="status"><Spinner animation="border" size="sm" className="me-2" />Loading modules…</div>) : (
                <>
                    <fieldset disabled={saving}>
                        <legend className="visually-hidden">Modules available to this company</legend>
                        {data.modules.length === 0 && <p className="text-muted">No modules are available in the permission catalog.</p>}
                        <div className="row g-3 mb-4">
                            {data.modules.map(module => <div className="col-12 col-md-6 col-xl-4" key={module.id}>
                                <div className="border rounded p-3 h-100">
                                    <Form.Check id={`module-access-${module.id}`} label={module.description} checked={draft.includes(Number(module.id))} onChange={event => toggle(Number(module.id), event.target.checked)} />
                                </div>
                            </div>)}
                        </div>
                    </fieldset>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <span role="status" className="text-muted">{saving ? 'Saving…' : hasChanges ? 'Unsaved changes' : refreshing ? 'Refreshing…' : `${draft.length} modules assigned`}</span>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" disabled={saving || refreshing || !hasChanges} onClick={() => refreshRef.current(true, true)}>Discard changes</Button>
                            <Button disabled={saving || !hasChanges} onClick={save}>Save changes</Button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
